/* eslint-disable array-bracket-newline */
import { useRef, useEffect } from 'react';
import { Chart, registerables } from 'chart.js';
import { roundToPrecision } from '../../../../helpers/truncate';

Chart.register(...registerables);

const COLORS_MAPPING = [
  'rgba(255, 69, 58, 0.8)',
  'rgba(255, 159, 10, 0.8)',
  'rgba(255, 214, 10, 0.8)',
  'rgba(48, 209, 88, 0.8)',
  'rgb(64, 101, 201, 0.8)',
];

const COLORS_HOVER_MAPPING = [
  'rgb(255, 69, 58)',
  'rgb(255, 159, 10)',
  'rgb(255, 214, 10)',
  'rgb(48, 209, 88)',
  'rgb(64, 101, 201)',
];

const OTHERS_COLOR = 'rgb(142, 142, 147, 0.8)';
const OTHERS_COLOR_HOVER = 'rgb(142, 142, 147)';

interface ITokensDistributionMobileProps {
  data: {
    label: string;
    value: number;
    key: string;
  }[];
}

const TokensDistributionMobile = ({ data }: ITokensDistributionMobileProps) => {
  const chartRef = useRef<Chart<'pie'> | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (chartRef.current && data.length) {
      const labels = data.map((item) => item.label);
      chartRef.current.data.labels = labels;
      const dataset = chartRef.current.data.datasets[0];
      const percents = data.map((item) => item.value);
      dataset.data = percents;
      chartRef.current.options.plugins!.tooltip!.callbacks = {
        title: function (tooltipItem) {
          return labels[tooltipItem[0].dataIndex];
        },
        label: function (tooltipItem) {
          return (
            roundToPrecision(percents[tooltipItem.dataIndex], true, 2) + '%'
          );
        },
      };

      chartRef.current.update();
    }
  }, [data]);

  useEffect(() => {
    window.requestIdleCallback(() => {
      if (canvasRef.current) {
        // Chart
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          chartRef.current = new Chart(ctx, {
            type: 'pie',
            data: {
              labels: [],
              datasets: [
                {
                  data: data.map(({ value }) => value),
                  backgroundColor: data.map((_, idx) =>
                    idx === data.length - 1 ? OTHERS_COLOR : COLORS_MAPPING[idx]
                  ),
                  hoverBackgroundColor: data.map((_, idx) =>
                    idx === data.length - 1
                      ? OTHERS_COLOR_HOVER
                      : COLORS_HOVER_MAPPING[idx]
                  ),
                  borderWidth: 0,
                },
              ],
            },
            options: {
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    title: function (tooltipItem) {
                      return data[tooltipItem[0].dataIndex].label;
                    },
                    label: function (tooltipItem) {
                      return (
                        roundToPrecision(
                          Number(data[tooltipItem.dataIndex].value),
                          true,
                          2
                        ) + '%'
                      );
                    },
                  },
                  bodySpacing: 5,
                  titleSpacing: 5,
                },
              },
            },
          });
        }
      }
    });

    return () => {
      chartRef.current?.destroy();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        height: '100%',
        maxHeight: '100%',
        minHeight: '100%',
      }}
    />
  );
};

export default TokensDistributionMobile;
